import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CalendarDays, Trash2, FileUp, Loader2, AlertCircle, Plus } from "lucide-react";
import { createPageUrl } from "@/utils";
import { usePortalFocus } from "@/hooks/usePortalFocus";
import { createPlanner } from "@/lib/aidPlannerSchema";
import { assessAidPlanner } from "@/lib/aidPlannerRules";
import { listingStage, plannerReminders } from "@/lib/grantReminders";
import GrantRemindersPanel from "@/components/GrantRemindersPanel";
import { getPortalToolPath } from "@/lib/portalToolRoutes";
import ApplicationsPanel from "@/components/aid-planner/ApplicationsPanel";
import { toast } from "sonner";
import { requestUpdatesForLetter, requestStatusLabel } from "@/lib/fileRequests";
import { fetchFileRequests, saveRequestStatus } from "@/lib/fileRequestsClient";
import { downloadPlannerIcs } from "@/lib/aidPlannerExport";
import {
  getLastOpenedMatterId,
  loadMatterFromDevice,
  saveMatterToDevice,
  setLastOpenedMatterId,
  listLocalMatters,
  matterInFirm,
  deleteLocalMatter,
  syncMattersWithPortal,
  getAidPlannerSyncStatus,
  AID_PLANNER_SYNC_EVENT,
} from "@/lib/aidPlannerStore";
import { parseAidLetterUpload } from "@/lib/aidLetterClientParser";
import PlannerActionsBar from "@/components/aid-planner/PlannerActionsBar";
import MatterDetailsTile from "@/components/aid-planner/MatterDetailsTile";
import FundingStatusTile from "@/components/aid-planner/FundingStatusTile";
import AppearanceClaimsTile from "@/components/aid-planner/AppearanceClaimsTile";
import FinalisationClosureTile from "@/components/aid-planner/FinalisationClosureTile";
import ActionsNotesTile from "@/components/aid-planner/ActionsNotesTile";
import ExtensionCheckTile from "@/components/aid-planner/ExtensionCheckTile";
import AtlasFetchPanel from "@/components/aid-planner/AtlasFetchPanel";
import RequisitionsBanner from "@/components/aid-planner/RequisitionsBanner";
import { applyRequisition, findRequisitionMatter, markRequisitionAnswered, openRequisitions } from "@/lib/atlasRequisitions";
import { atlasClientName } from "@/lib/atlasLettersExtension";
import PracticeManagerImportModal from "@/components/aid-planner/PracticeManagerImportModal";
import { getPracticeManagerAdapter, practiceManagerAdapters } from "@/lib/aidPlannerAdapters";
import { createAppearanceClaim } from "@/lib/aidPlannerSchema";
import { applyPlannerAppearanceFeeDefaults, getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";
import { inferCriminalAidPathway } from "@/lib/criminalAidFramework";
import { deriveGrantActionAfterOutcome, inferOutcomeFromText } from "@/lib/aidPlannerAppearanceImport";

function updateTimestamp(data) {
  return { ...data, updatedAt: new Date().toISOString() };
}

function formatProfessionalCostsList(list = []) {
  if (!Array.isArray(list) || list.length === 0) return "";
  const cleaned = list.map(item => `${item || ""}`.trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(", ")} and ${cleaned[cleaned.length - 1]}`;
}

function applyInferredCriminalPathway(nextPlanner) {
  const inferredPathway = inferCriminalAidPathway({
    matterType: nextPlanner.matter.matterType,
    court: nextPlanner.matter.court,
    appearanceType: nextPlanner.matter.appearanceType,
    appealCostsCertificateGranted: nextPlanner.criminal.appealCostsCertificateGranted,
  });

  if (!inferredPathway) {
    if (nextPlanner.matter.matterType !== "Criminal") {
      return {
        ...nextPlanner,
        criminal: {
          ...nextPlanner.criminal,
          pathwayId: "",
          pathwayLabel: "",
          feeTableRef: "",
          feeScheduleRef: "",
        },
      };
    }
    return nextPlanner;
  }

  return {
    ...nextPlanner,
    criminal: {
      ...nextPlanner.criminal,
      pathwayId: inferredPathway.id,
      pathwayLabel: inferredPathway.label,
      feeTableRef: inferredPathway.feeTableRef,
      feeScheduleRef: inferredPathway.feeScheduleRef,
    },
  };
}

function syncFundingFromApplicationStatus(planner, applicationStatus) {
  const nextFunding = { ...planner.funding };
  const nextApplication = { ...planner.application };

  const isApproved =
    applicationStatus === "Approved" ||
    applicationStatus === "Approved with a caveat" ||
    applicationStatus === "Approved with a contribution" ||
    applicationStatus === "Aid granted";

  const isPending =
    applicationStatus === "Pending" ||
    applicationStatus === "Requisition to be answered" ||
    applicationStatus === "Application applied for - Pending" ||
    applicationStatus === "Aid submitted - application pending" ||
    applicationStatus === "Aid applied for";

  const isRefused =
    applicationStatus === "Refused" ||
    applicationStatus === "Aid refused";

  if (isApproved) {
    nextFunding.aidApplied = true;
    nextFunding.grantInPlace = true;
    nextApplication.lodgedInAtlas = true;
    nextApplication.decisionReceived = true;
    nextApplication.decisionResult = "Granted";
  } else if (isPending) {
    nextFunding.aidApplied = true;
    nextFunding.grantInPlace = false;
    nextApplication.lodgedInAtlas = true;
    nextApplication.decisionReceived = false;
    nextApplication.decisionResult = "Pending";
  } else if (isRefused) {
    nextFunding.aidApplied = true;
    nextFunding.grantInPlace = false;
    nextApplication.lodgedInAtlas = true;
    nextApplication.decisionReceived = true;
    nextApplication.decisionResult = "Refused";
  } else {
    nextFunding.aidApplied = false;
    nextFunding.grantInPlace = false;
    nextApplication.lodgedInAtlas = false;
    nextApplication.decisionReceived = false;
    nextApplication.decisionResult = "";
  }

  return {
    ...planner,
    funding: nextFunding,
    application: nextApplication,
  };
}

function applyImportedMatter(prev, adapter, selectedMatter) {
  if (!adapter || !selectedMatter) return prev;

  const client = {
    ...prev.client,
    ...selectedMatter.client,
    fullName:
      selectedMatter.client?.fullName ||
      [selectedMatter.client?.firstName, selectedMatter.client?.lastName].filter(Boolean).join(" "),
  };

  const nextAppearanceDate =
    selectedMatter.matter?.nextAppearanceDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedMatter.matter.nextAppearanceDate)
      ? selectedMatter.matter.nextAppearanceDate
      : prev.matter.nextAppearanceDate;

  return updateTimestamp({
    ...prev,
    source: {
      type: "pms",
      provider: adapter.name,
      externalId: selectedMatter.externalId || "",
      actionstep: selectedMatter.actionstep || {},
    },
    client,
    matter: {
      ...prev.matter,
      ...selectedMatter.matter,
      nextAppearanceDate,
    },
  });
}

function extractClientName(file, payload) {
  const text = payload?.rawText || "";
  
  const appDetailsMatch = text.match(/Applicant\s+Details[\s\S]*?Name\s+([A-Z\s\-',]+?)(?:\r?\n|$)/i);
  if (appDetailsMatch && appDetailsMatch[1].trim()) {
    const cleaned = appDetailsMatch[1].replace(/[\r\n]/g, " ").replace(/\s+/g, " ").trim();
    if (cleaned.length > 2 && cleaned.length < 50) return cleaned;
  }
  
  const appNameMatch = text.match(/Applicant(?:'s)?\s+Name\s*:\s*([A-Za-z\s\-',]+)/i);
  if (appNameMatch && appNameMatch[1].trim()) {
    const cleaned = appNameMatch[1].replace(/[\r\n]/g, " ").replace(/\s+/g, " ").trim();
    if (cleaned.length > 2 && cleaned.length < 50) return cleaned;
  }

  const matterOfMatch = text.match(/In\s+the\s+matter\s+of\s*:\s*([A-Za-z\s\-',]+)/i);
  if (matterOfMatch && matterOfMatch[1].trim()) {
    const cleaned = matterOfMatch[1].replace(/[\r\n]/g, " ").replace(/\s+/g, " ").trim();
    if (cleaned.length > 2 && cleaned.length < 50) return cleaned;
  }

  const reMatch = text.match(/Re\s*:\s*([A-Za-z\s\-',]+?)(?:\s+Extension|\s+Aid|\s+Application|\r?\n|$)/i);
  if (reMatch && reMatch[1].trim()) {
    const cleaned = reMatch[1].replace(/[\r\n]/g, " ").replace(/\s+/g, " ").trim();
    if (cleaned.length > 2 && cleaned.length < 50) return cleaned;
  }

  if (file && file.name) {
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const cleanedName = nameWithoutExt
      .replace(/[-_]/g, " ")
      .replace(/\b(vla|grant|letter|approval|app|request|matter|planner)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    if (cleanedName) {
      return cleanedName.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    }
  }

  return "Unnamed Client";
}

function formatFriendlyDate(dateStr) {
  if (!dateStr) return "";
  if (/[a-zA-Z]/.test(dateStr) && dateStr.split(" ").length === 3) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${day} ${months[monthIndex]} ${year}`;
    }
  }
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  return dateStr;
}

function mergeParsedFieldsIntoPlanner(prev, payload, fileName, fileData) {
  const fields = payload?.fields || {};
  const isApprovalLetter = fields.documentType === "grant_approval_letter";
  
  const nextUploadedLetterName =
    fileName && prev.aid.uploadedLetterName && prev.aid.uploadedLetterName !== fileName
      ? `${prev.aid.uploadedLetterName} | ${fileName}`
      : fileName || prev.aid.uploadedLetterName;

  const letterType = fields.documentType;
  let description = "Letter uploaded";
  const rawText = payload?.rawText || "";
  const rawLowerForDetect = rawText.toLowerCase();

  // Helper: strip leading "EXT - " or "EXT-" prefix from aidType if present (we'll add "EXT N - " ourselves)
  const cleanAidType = (raw) => {
    if (!raw) return "";
    const stripped = raw.replace(/^ext\s*[-–]\s*/i, "").trim();
    // Title-case each word for display
    return stripped.replace(/\b\w/g, c => c.toUpperCase());
  };

  const letterExtNum = fields.extensionNumber !== undefined && fields.extensionNumber !== null ? String(fields.extensionNumber) : "";
  const isExtension = letterExtNum !== "" && letterExtNum !== "0";

  // --- ATLAS claim form signals: these must win over any letterType classification ---
  // ATLAS claim forms contain VLA grant numbers causing them to be misclassified.
  // IMPORTANT: application_request documents also contain CRSM codes in their scope section —
  // never treat them as claim forms regardless of their text content.
  const isAtlasClaimForm =
    fields.documentType !== "application_request" &&
    (
      rawLowerForDetect.includes("work items") ||
      rawLowerForDetect.includes("this claim") ||
      rawLowerForDetect.includes("units to be claimed") ||
      rawLowerForDetect.includes("claim summary") ||
      rawLowerForDetect.includes("effective date range") ||
      rawLowerForDetect.includes("recipient created tax invoice") ||
      fileName.toLowerCase().includes("claim") ||
      fileName.toLowerCase().includes("invoice")
    );

  if (isAtlasClaimForm) {
    description = "Billing/Claim letter";
  } else if (letterType === "grant_approval_letter") {
    if (isExtension) {
      const existingExt = (prev.aid.extensions || []).find(e => String(e.extensionNumber) === letterExtNum);
      const aidType = cleanAidType(existingExt?.aidType || fields.aidType || "");
      description = aidType ? `EXT ${letterExtNum} - ${aidType} approved` : `Extension ${letterExtNum} approved`;
    } else {
      description = "Aid approved";
    }
  } else if (letterType === "application_request") {
    if (isExtension) {
      const existingExt = (prev.aid.extensions || []).find(e => String(e.extensionNumber) === letterExtNum);
      const aidType = cleanAidType(existingExt?.aidType || fields.aidType || "");
      description = aidType ? `EXT ${letterExtNum} - ${aidType} applied` : `Extension ${letterExtNum} applied for`;
    } else {
      const hasSummary = (fields.aidType || "").toLowerCase().includes("summary") ||
                        rawLowerForDetect.includes("summary");
      const aidTypeLabel = cleanAidType(fields.aidType || "");
      if (hasSummary) {
        description = "Summary aid applied for";
      } else if (aidTypeLabel) {
        description = `${aidTypeLabel} applied for`;
      } else {
        description = "Aid applied for";
      }
    }
  } else if (
    letterType === "claim_letter" ||
    rawLowerForDetect.includes("claim") ||
    rawLowerForDetect.includes("invoice") ||
    rawLowerForDetect.includes("prep")
  ) {
    description = "Billing/Claim letter";
  }

  // Use the most meaningful date for each letter type
  let letterDate;
  if (letterType === "grant_approval_letter") {
    letterDate = fields.effectiveDate || new Date().toISOString().split("T")[0];
  } else if (letterType === "application_request") {
    letterDate = fields.dateApplied || fields.listingDate || new Date().toISOString().split("T")[0];
  } else {
    letterDate = fields.effectiveDate || fields.listingDate || new Date().toISOString().split("T")[0];
  }
  
  const letterId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
  const newLetterItem = {
    id: letterId,
    fileName,
    description,
    date: formatFriendlyDate(letterDate),
    isoDate: letterDate,
    uploadedAt: new Date().toISOString(),
    fileData: fileData || ""
  };

  const nextUploadedLetters = [...(prev.uploadedLetters || []), newLetterItem];

  let mutableExtNum = letterExtNum;
  if (isApprovalLetter && mutableExtNum === "") {
    mutableExtNum = "0";
  }
  let nextExtensions = Array.isArray(prev.aid.extensions) ? [...prev.aid.extensions] : [];

  const isApprovedStatus = (status = "") => {
    const s = status.toLowerCase();
    return s.includes("grant") || s.includes("approved") || s.includes("refuse") || s.includes("reject");
  };

  if (mutableExtNum !== "") {
    const existingIndex = nextExtensions.findIndex((ext) => String(ext.extensionNumber) === mutableExtNum);
    const newExt = {
      extensionNumber: mutableExtNum,
      aidType: fields.aidType || "",
      aidTypeList: fields.aidTypeList || [],
      dateApplied: fields.dateApplied || fields.listingDate || "",
      effectiveDate: fields.effectiveDate || "",
      applicationStatus: fields.applicationStatus || "Aid submitted - application pending",
    };

    if (existingIndex >= 0) {
      const existingExt = nextExtensions[existingIndex];
      // Always update the extension's own status from the letter (no guard here)
      // The overall aid.applicationStatus is protected separately
      const updatedStatus = newExt.applicationStatus || existingExt.applicationStatus;

      nextExtensions[existingIndex] = {
        ...existingExt,
        aidType: newExt.aidType || existingExt.aidType,
        aidTypeList: newExt.aidTypeList?.length ? newExt.aidTypeList : existingExt.aidTypeList,
        dateApplied: newExt.dateApplied || existingExt.dateApplied,
        effectiveDate: newExt.effectiveDate || existingExt.effectiveDate,
        applicationStatus: updatedStatus,
      };
    } else {
      nextExtensions.push(newExt);
    }

    nextExtensions.sort((a, b) => {
      const numA = parseInt(a.extensionNumber) || 0;
      const numB = parseInt(b.extensionNumber) || 0;
      return numA - numB;
    });
  }

  // --- Status logic ---
  // The overall aid.applicationStatus should only be updated by the initial application (ext 0)
  // or if the new letter promotes the status (to approved/refused). Never downgrade an approved status.
  const incomingStatus = fields.applicationStatus;
  const existingStatus = prev.aid.applicationStatus;

  // Status for the specific extension record being uploaded
  const extRecordStatus = incomingStatus || "Aid submitted - application pending";

  // Status for the overall aid — protected once approved; only update for ext 0 or promotions
  const isInitialApplication = !isExtension; // ext 0 or no ext number
  const overallStatus = (() => {
    if (!incomingStatus) return existingStatus || "Aid submitted - application pending";
    if (isApprovedStatus(existingStatus) && !isApprovedStatus(incomingStatus)) {
      // Already approved — do not downgrade for extension uploads
      return existingStatus;
    }
    if (isInitialApplication || isApprovedStatus(incomingStatus)) {
      return incomingStatus;
    }
    // Extension letter with non-approved status — keep the overall status unchanged
    return existingStatus || incomingStatus;
  })();

  const activeExt = nextExtensions[nextExtensions.length - 1] || {
    extensionNumber: fields.extensionNumber || prev.aid.extensionNumber || "",
    aidType: fields.aidType || prev.aid.aidType,
    aidTypeList: fields.aidTypeList || prev.aid.aidTypeList,
    dateApplied: fields.dateApplied || fields.listingDate || prev.application.dateApplied || "",
    effectiveDate: fields.effectiveDate || prev.aid.effectiveDate,
    applicationStatus: extRecordStatus,
  };


  // --- Claim/billing letters: record upload + update appearance tracker ---
  const isClaimLetter =
    letterType === "claim_letter" ||
    description === "Billing/Claim letter" ||
    rawText.toLowerCase().includes("work items") ||
    rawText.toLowerCase().includes("this claim") ||
    rawText.toLowerCase().includes("crsm") ||
    rawText.toLowerCase().includes("claim summary") ||
    rawText.toLowerCase().includes("units to be claimed");

  if (isClaimLetter) {
    const today = new Date().toISOString().split("T")[0];
    const claimId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2, 15);

    // --- Extract appearance date and type from raw text ---
    const rawLower = (payload?.rawText || "").toLowerCase();

    let appearanceIsoDate = "";
    let isConsolidatedClaim = false;
    const monthNames = { jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12" };

    // PRIORITY 1: Services Date — start of "DD/MM/YYYY - DD/MM/YYYY" range near "services date"
    const servicesSectionMatch = rawLower.match(/services\s*date[\s\S]{0,400}?(\d{1,2})\/(\d{1,2})\/(\d{4})\s*[-–]\s*\d{1,2}\/\d{1,2}\/\d{4}/);
    if (servicesSectionMatch) {
      const [, d, m, y] = servicesSectionMatch;
      appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
    }

    // PRIORITY 0: ATLAS "Effective Date Range" column — consolidated claim forms
    // The ATLAS form has: "Effective Date Range\n27/05/2026 to Current"
    const effectiveDateMatch = rawLower.match(/effective\s+date\s+range[\s\S]{0,100}?(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (effectiveDateMatch) {
      const [, d, m, y] = effectiveDateMatch;
      appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
      isConsolidatedClaim = true;
    }

    // PRIORITY 0.5: RCTI "Service Date DD/MM/YYYY" — single date, no range
    if (!appearanceIsoDate) {
      const rctiServiceDate = rawLower.match(/service\s+(?:date|period)[^0-9]{0,30}?(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (rctiServiceDate) {
        const [, d, m, y] = rctiServiceDate;
        appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
      }
    }

    // PRIORITY 1b: "DD/MM/YYYY to current" — consolidated/extension claims cover a date range
    // Require "current" to appear within 5 chars of "to" to avoid false matches
    if (!appearanceIsoDate) {
      const toCurrentMatch = rawLower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(?:to|-|\u2013)\s{0,5}current/);
      if (toCurrentMatch) {
        const [, d, m, y] = toCurrentMatch;
        appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
        isConsolidatedClaim = true; // date range = consolidated / extension claim
      }
    }

    // PRIORITY 2: Any DD/MM/YYYY - DD/MM/YYYY range anywhere in the document
    if (!appearanceIsoDate) {
      const anyRange = rawLower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*[-–]\s*\d{1,2}\/\d{1,2}\/\d{4}/);
      if (anyRange) {
        const [, d, m, y] = anyRange;
        appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
      }
    }

    // PRIORITY 3: First slash date in the "work performed" section
    if (!appearanceIsoDate) {
      const workIdx = rawLower.indexOf("work performed");
      if (workIdx !== -1) {
        const workSection = rawLower.slice(workIdx, workIdx + 600);
        const slashDate = workSection.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (slashDate) {
          const [, d, m, y] = slashDate;
          appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
        }
      }
    }

    // PRIORITY 4: General fallbacks
    if (!appearanceIsoDate) {
      const wordDateMatch = rawLower.match(/(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{4})/);
      const slashDateMatch = rawLower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      const isoDateMatch = rawLower.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (wordDateMatch) {
        const [, day, mon, yr] = wordDateMatch;
        appearanceIsoDate = `${yr}-${monthNames[mon]}-${day.padStart(2, "0")}`;
      } else if (slashDateMatch) {
        const [, d, m, y] = slashDateMatch;
        appearanceIsoDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
      } else if (isoDateMatch) {
        appearanceIsoDate = isoDateMatch[0];
      }
    }

    // --- Detect work/appearance type(s) ---
    // For single-appearance claims: read the primary "Work Type" label field only
    // For consolidated claims ("to current"): scan ATLAS work type table rows (CRSM-prefixed lines)
    let detectedTypes = ["Other"];

    if (isConsolidatedClaim) {
      // Consolidated claim — look for ATLAS work type table rows (lines containing CRSM codes)
      const atlasWorkTypes = [
        { key: "preparation",             label: "Preparation" },
        { key: "brief sentence indication", label: "Plea" },
        { key: "appear at sentence",      label: "Plea" },
        { key: "plea at contest mention", label: "Contest Mention" },
        { key: "contest mention",         label: "Contest Mention" },
        { key: "special mention",         label: "Special Mention" },
        { key: "committal",               label: "Committal Mention" },
        { key: "adjournment",             label: "Adjournment" },
        { key: "mention",                 label: "Mention" },
        { key: "bail",                    label: "Bail" },
        { key: "contest",                 label: "Contest" },
        { key: "plea",                    label: "Plea" },
        { key: "appearance",              label: "Appearance" },
        { key: "sentence",                label: "Plea" },
      ];
      // Prefer scanning CRSM-coded table rows; fall back to full document scan
      const crsLines = rawLower.split("\n").filter(l => /crsm/i.test(l));
      const tableText = crsLines.length > 0 ? crsLines.join(" ") : rawLower;
      const found = [];
      for (const { key, label } of atlasWorkTypes) {
        if (tableText.includes(key) && !found.includes(label)) {
          found.push(label);
        }
      }
      if (found.length > 0) detectedTypes = found;
    } else {
      // Single-appearance claim — detect primary type from "Work Type" field or claim description
      const workTypeMatch = rawLower.match(/work\s*type[\s\S]{0,100}?\n([a-z ]+)\n/);
      const workTypeRaw = workTypeMatch?.[1]?.trim() || "";
      let primary = "Other";
      if (workTypeRaw.includes("preparation") || rawLower.includes("prep fee") || rawLower.includes("preparation")) {
        primary = "Preparation";
      } else if (workTypeRaw.includes("adjournment") || rawLower.match(/\badjournment\b/)) {
        primary = "Adjournment";
      } else if (workTypeRaw.includes("contest mention") || rawLower.includes("contest mention")) {
        primary = "Contest Mention";
      } else if (workTypeRaw.includes("mention") || rawLower.includes("mention")) {
        primary = "Mention";
      } else if (workTypeRaw.includes("plea") || rawLower.match(/\bplea\b/)) {
        primary = "Plea";
      } else if (workTypeRaw.includes("contest") || rawLower.match(/\bcontest\b/)) {
        primary = "Contest";
      } else if (workTypeRaw.includes("bail") || rawLower.match(/\bbail\b/)) {
        primary = "Bail";
      } else if (workTypeRaw.includes("special mention") || rawLower.includes("special mention")) {
        primary = "Special Mention";
      } else if (workTypeRaw.includes("committal") || rawLower.includes("committal")) {
        primary = "Committal Mention";
      } else if (workTypeRaw.includes("sentence") || rawLower.includes("sentencing")) {
        primary = "Plea";
      }
      detectedTypes = [primary];
    }

    const detectedAppearanceType = detectedTypes[0];

    // For extension claims, prefix tracker types to match the correct grant-specific rows
    // and prevent collision with Single Summary rows of the same generic name.
    // Primary: extension approval letter aidType (upload rule ensures it's present first)
    // Secondary: raw RCTI text as fallback signal
    let trackerTypes = detectedTypes;
    if (isExtension && letterExtNum !== "" && letterExtNum !== "0") {
      const extRecord = (prev.aid.extensions || []).find(e => String(e.extensionNumber) === letterExtNum);
      const extAidTypeRaw = (extRecord?.aidType || fields.aidType || "").toLowerCase();

      if (extAidTypeRaw.includes("consol") || rawLower.includes("consol prep") || rawLower.includes("crsm8")) {
        // Consolidated extension uplift fees
        const consolTypes = detectedTypes
          .filter(t => t !== "Other")
          .map(t => `CONSOL ${t.toUpperCase()}`);
        trackerTypes = consolTypes.length > 0 ? consolTypes : ["Other"];
      } else if (extAidTypeRaw.includes("bail") || rawLower.includes("bail preparation") || rawLower.includes("bail appearance")) {
        // CRSM60 — Bail extension fees
        const bailTypes = detectedTypes
          .filter(t => t !== "Other")
          .map(t => `BAIL ${t.toUpperCase()}`);
        trackerTypes = bailTypes.length > 0 ? bailTypes : ["Other"];
      }
    }

    // --- Build friendly display label ---
    const longMonthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

    // For extension claims, prefix the label with the extension aid type (e.g. "EXT 1 - Increase to Consol")
    let claimPrefix = "";
    if (isExtension) {
      const extRecord = (prev.aid.extensions || []).find(e => String(e.extensionNumber) === letterExtNum);
      const extAidType = cleanAidType(extRecord?.aidType || fields.aidType || "");
      if (extAidType) {
        claimPrefix = `EXT ${letterExtNum} - ${extAidType} `;
      }
    }

    const claimedWork = (fields.claimItems || []).map((item) => item.workType.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase()));
    const typeLabel = claimedWork.length > 0
      ? `${claimedWork.join(", ")} Claim`
      : detectedAppearanceType !== "Other" ? `${detectedAppearanceType} Claim` : "Claim";
    let friendlyLabel = `${claimPrefix}${typeLabel}`;
    if (appearanceIsoDate) {
      const [fy, fm, fd] = appearanceIsoDate.split("-");
      const dateStr = `${parseInt(fd)} ${longMonthNames[parseInt(fm) - 1]} ${fy}`;
      friendlyLabel = isConsolidatedClaim
        ? `${friendlyLabel} from ${dateStr}`   // e.g. "EXT 1 - Increase to Consol Claim from 27 May 2026"
        : `${friendlyLabel} - ${dateStr}`;      // e.g. "Adjournment Claim - 13 February 2026"
    }

    // --- Extract the ATLAS claim submission date (different from the appearance/services date) ---
    let claimDateFormatted = "";
    const allSlashDates = [...rawLower.matchAll(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g)].map(m => {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
    });
    const claimIsoDate = allSlashDates.find(d => d !== appearanceIsoDate) || "";
    if (claimIsoDate) {
      const [cy, cm, cd] = claimIsoDate.split("-");
      claimDateFormatted = `${parseInt(cd)} ${longMonthNames[parseInt(cm) - 1]} ${cy}`;
    }
    const atlasNote = claimDateFormatted
      ? `Claimed through ATLAS on ${claimDateFormatted}`
      : `Claimed through ATLAS`;

    // --- Build the upload history item ---
    const hasMissingApproval =
      isExtension &&
      letterExtNum !== "" &&
      letterExtNum !== "0" &&
      !(prev.aid.extensions || []).find(
        (e) => String(e.extensionNumber) === letterExtNum && (e.aidType || e.applicationStatus)
      );

    const claimItem = {
      id: claimId,
      fileName,
      description: friendlyLabel,
      date: formatFriendlyDate(today),
      isoDate: today,
      uploadedAt: new Date().toISOString(),
      fileData: fileData || "",
      ...(hasMissingApproval
        ? { missingApprovalWarning: true, claimExtensionNumber: letterExtNum }
        : {}),
    };

    // --- Update appearance tracker — one entry per detected work type ---
    const existingClaims = Array.isArray(prev.appearanceClaims) ? [...prev.appearanceClaims] : [];
    let updatedAppearanceClaims = [...existingClaims];

    // An ATLAS claim lists each fee in VLA's own fee names, which are the names the allowance
    // rows use, so each one is recorded as claimed against that fee exactly.
    const claimItems = Array.isArray(fields.claimItems) ? fields.claimItems : [];
    // A claim can list the same fee more than once (a contest day per row), so each row is
    // its own claim: a row already recorded is recognised by claim ID and row number (or, for
    // claims recorded before rows were numbered, by fee and date).
    for (const [row, item] of claimItems.entries()) {
      const date = item.serviceDate || appearanceIsoDate;
      const already = updatedAppearanceClaims.some(
        (c) => c.atlasClaimId && c.atlasClaimId === fields.claim?.claimId && c.feeType === item.workType
          && (c.atlasClaimRow === undefined ? c.date === date : c.atlasClaimRow === row)
      );
      if (already) continue;
      updatedAppearanceClaims = [
        ...updatedAppearanceClaims,
        {
          id: (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : Math.random().toString(36).substring(2, 15),
          date,
          appearanceType: item.workType,
          customAppearanceType: "",
          outcome: "",
          nextListingType: "",
          grantActionRequired: "",
          feeType: item.workType,
          feeAmount: item.amount != null ? String(item.amount) : "",
          feeTable: "",
          feeNote: fields.claim?.claimId ? `ATLAS claim ${fields.claim.claimId}` : "",
          invoiceFileName: fileName,
          claimedUnits: item.units || "",
          claimExpected: false,
          claimLodged: true,
          claimPaid: false,
          importSource: "claim_letter",
          atlasClaimId: fields.claim?.claimId || "",
          atlasClaimRow: row,
          notes: atlasNote,
        },
      ];
    }

    for (const workType of claimItems.length > 0 ? [] : trackerTypes) {
      const matchIdx = appearanceIsoDate
        ? updatedAppearanceClaims.findIndex(
            (c) => (c.date === appearanceIsoDate || c.date === formatFriendlyDate(appearanceIsoDate))
                   && c.appearanceType === workType
          )
        : -1;

      if (matchIdx >= 0) {
        updatedAppearanceClaims = updatedAppearanceClaims.map((c, i) =>
          i === matchIdx ? { ...c, claimLodged: true, invoiceFileName: fileName, notes: atlasNote } : c
        );
      } else {
        const newAppearanceId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
          ? crypto.randomUUID()
          : Math.random().toString(36).substring(2, 15);
        updatedAppearanceClaims = [
          ...updatedAppearanceClaims,
          {
            id: newAppearanceId,
            date: appearanceIsoDate,
            appearanceType: workType,
            customAppearanceType: "",
            outcome: workType === "Preparation" ? "" : "Adjourned",
            nextListingType: "",
            grantActionRequired: "",
            feeType: "",
            feeAmount: "",
            feeTable: "",
            feeNote: "",
            invoiceFileName: fileName,
            claimedUnits: "",
            claimExpected: false,
            claimLodged: true,
            claimPaid: false,
            importSource: "claim_letter",
            notes: atlasNote,
          },
        ];
      }
    }


    return {
      ...prev,
      uploadedLetters: [...(prev.uploadedLetters || []), claimItem],
      appearanceClaims: updatedAppearanceClaims,
      // A matter first met through a claim takes the claim's client and grant number; a
      // matter that already has them keeps its own.
      client: {
        ...prev.client,
        fullName: prev.client?.fullName || fields.clientFullName || "",
        firstName: prev.client?.firstName || fields.clientFirstName || "",
        lastName: prev.client?.lastName || fields.clientLastName || "",
      },
      aid: {
        ...prev.aid,
        aidNumber: prev.aid.aidNumber || fields.aidNumber || "",
        // A claim for a psychological report means the disbursement was approved; load its
        // allowance rows so the claim counts against them.
        aidTypeList: (fields.claimItems || []).some((item) => /^PSYCHOL/.test(item.workType))
          && !(prev.aid.aidTypeList || []).some((type) => /psychol/i.test(type || ""))
          ? [...(prev.aid.aidTypeList || []), "Psychological report (DISB51)"]
          : prev.aid.aidTypeList,
        uploadedLetterName: prev.aid.uploadedLetterName
          ? `${prev.aid.uploadedLetterName} | ${friendlyLabel}`
          : friendlyLabel,
      },
      updatedAt: new Date().toISOString(),
    };
  }


  const merged = {
    ...prev,
    uploadedLetters: nextUploadedLetters,
    client: {
      ...prev.client,
      firstName: fields.clientFirstName || prev.client.firstName,
      lastName: fields.clientLastName || prev.client.lastName,
      fullName: fields.clientFullName || prev.client.fullName,
      // fileNumber must come from the application letter only — never from approvals or claims.
      // Also sanitise: reject page-footer values like "Page 8 of 8".
      fileNumber: (() => {
        if (letterType !== "application_request") return prev.client.fileNumber;
        const raw = (fields.practitionerReference || "").trim();
        if (!raw) return prev.client.fileNumber;
        if (/^page\s+\d+\s+of\s+\d+$/i.test(raw)) return prev.client.fileNumber; // page footer
        if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(raw)) return prev.client.fileNumber; // date
        if (raw.split(/\s+/).length > 4) return prev.client.fileNumber; // sentence, not a ref
        return raw;
      })(),
      dateOfBirth: fields.clientDateOfBirth || prev.client.dateOfBirth,
    },
    matter: {
      ...prev.matter,
      matterType: isApprovalLetter ? (prev.matter.matterType || fields.matterType) : (fields.matterType || prev.matter.matterType),
      court: isApprovalLetter ? (prev.matter.court || fields.court) : (fields.court || prev.matter.court),
      location: isApprovalLetter ? (prev.matter.location || fields.location) : (fields.location || prev.matter.location),
      appearanceType: isApprovalLetter ? (prev.matter.appearanceType || fields.listingType) : (fields.listingType || prev.matter.appearanceType),
      nextAppearanceDate: isApprovalLetter ? (prev.matter.nextAppearanceDate || fields.listingDate) : (fields.listingDate || prev.matter.nextAppearanceDate),
      isCustody: fields.custodyStatus ? (fields.custodyStatus.toUpperCase() === "YES") : prev.matter.isCustody,
      headCharge: fields.headCharge || prev.matter.headCharge,
    },
    aid: {
      ...prev.aid,
      // --- FROZEN initial grant fields ---
      // Aid is built in layers: initial grant + extensions. The initial grant fields
      // are set once from the initial application/approval and NEVER changed by extensions.
      // Each extension stores its own data in planner.aid.extensions[n].
      // Prefer the existing aidType heading (set by the application request, e.g. "ADULT - INDICTABLE...").
      // Only fall back to the incoming letter value if nothing has been set yet.
      aidType: isInitialApplication ? (prev.aid.aidType || fields.aidType) : prev.aid.aidType,
      // Union merge: accumulate all grant types across application + approval letters.
      // Approval letters often mention only one type (e.g. "Bail Application") but the
      // application request has the full scope — never let an approval letter shrink the list.
      aidTypeList: isInitialApplication
        ? (() => {
            const existing = prev.aid.aidTypeList || [];
            const incoming = Array.isArray(fields.aidTypeList) ? fields.aidTypeList : [];
            const merged = [...new Set([...existing, ...incoming])];
            return merged.length > 0 ? merged : existing;
          })()
        : prev.aid.aidTypeList,
      aidNumber: isInitialApplication ? (fields.aidNumber || prev.aid.aidNumber) : prev.aid.aidNumber,
      effectiveDate: isInitialApplication ? (fields.effectiveDate || prev.aid.effectiveDate) : prev.aid.effectiveDate,
      // --- Fields that may update from any letter ---
      guideline: isApprovalLetter ? (prev.aid.guideline || fields.guideline) : (fields.guideline || prev.aid.guideline),
      // extensionNumber tracks the highest extension present (still needs to increment)
      extensionNumber: activeExt.extensionNumber,
      applicationStatus: overallStatus,
      uploadedLetterName: nextUploadedLetterName,
      custodyStatus: isApprovalLetter ? (prev.aid.custodyStatus || fields.custodyStatus) : (fields.custodyStatus || prev.aid.custodyStatus),
      documentType: fields.documentType || prev.aid.documentType,
      parsedAlerts: Array.isArray(payload?.parsedAlerts) ? payload.parsedAlerts : [],
      conflictFlags: Array.isArray(payload?.conflictFlags) ? payload.conflictFlags : [],
      extensions: nextExtensions,
      // What each approval letter approved, and on what terms. Kept across letters, so the
      // original grant and every extension stay on the matter.
      approvedGrants: (() => {
        const incoming = (fields.approvedGrants || []).map((grant) => ({
          ...grant,
          aidNumber: fields.aidNumber || "",
          extensionNumber: fields.extensionNumber || "0",
          effectiveDate: fields.effectiveDate || "",
        }));
        const key = (grant) => [grant.aidNumber, grant.extensionNumber, grant.feeTable, grant.aidType].join("|");
        const byKey = new Map([...(prev.aid.approvedGrants || []), ...incoming].map((grant) => [key(grant), grant]));
        return [...byKey.values()];
      })(),
      grantExclusions: [...new Set([...(prev.aid.grantExclusions || []), ...(fields.grantExclusions || [])])],
      grantConditions: (() => {
        const byKind = new Map([...(prev.aid.grantConditions || []), ...(fields.grantConditions || [])].map((condition) => [condition.kind, condition]));
        return [...byKind.values()];
      })(),
      reconsiderationBy: fields.reconsiderationBy || prev.aid.reconsiderationBy,
      contribution: fields.contribution || prev.aid.contribution,
    },
    application: {
      ...prev.application,
      // Only update dateApplied from application request letters — never from approval letters or extensions
      dateApplied: (isInitialApplication && !isApprovalLetter) ? (fields.dateApplied || prev.application.dateApplied) : prev.application.dateApplied,
    },
  };

  // An extension approval letter records the extension as requested and approved. It covers
  // the rest of the stage it was sought for (the contest, the committal...): no "all fees
  // used" reminder for any listing in that stage. The next stage gets its own check.
  if (isApprovalLetter && isExtension) {
    merged.funding = {
      ...prev.funding,
      extensionRequested: true,
      extensionDecision: "Approved",
      extensionApprovedForListing: prev.matter?.nextAppearanceDate || "",
      extensionApprovedForStage: listingStage(prev.matter?.appearanceType),
    };
  }
  return merged;
}

// embedded: shown as a tab inside a firm's portal, which has its own header and navigation,
// so the page's own "Back to Portal" links and LACW logo are left out.
// brand: the firm whose portal it is shown in. LACW's also shows its file requests.
export default function AidPlanner({ embedded = false, brand = "" }) {
  const [planner, setPlanner] = useState(() => createPlanner());
  const [viewMode, setViewMode] = useState("dashboard"); // "dashboard" or "editor"
  // A matter opened from the morning summary. Kept until it is open: a matter not yet on this
  // device opens once the sync below brings it.
  const focus = usePortalFocus("matter", brand);
  const focusMatterId = useRef(focus?.id || "");
  const openFocusMatter = () => {
    if (!focusMatterId.current) return false;
    const matter = loadMatterFromDevice(focusMatterId.current);
    if (!matterInFirm(matter, brand)) return false;
    focusMatterId.current = "";
    setPlanner(matter);
    setViewMode("editor");
    return true;
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [isParsingBulk, setIsParsingBulk] = useState(false);
  // Held here, not in the panel, so the results survive opening a matter and coming back.
  const [atlasRun, setAtlasRun] = useState(null);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkParseSummary, setBulkParseSummary] = useState(null);
  const [mattersTrigger, setMattersTrigger] = useState(0);
  const unifiedUploaderRef = useRef(null);
  const [isParsingLetter, setIsParsingLetter] = useState(false);
  const [letterError, setLetterError] = useState("");

  // Matters are listed, synced and saved for this firm. An administrator opening a firm's
  // Aid Planner files new matters under that firm, so the firm's staff see them.
  const saveMatter = (matter) => saveMatterToDevice(matter, { firm: brand });
  const localMatters = useMemo(() => {
    return listLocalMatters(brand);
  }, [mattersTrigger]);
  const requisitionsOpen = useMemo(() => openRequisitions(localMatters), [localMatters]);

  // Matters are shared with the firm through the portal. Bring this computer into step when
  // the page opens, when someone comes back to it, and every minute while it is open.
  // LACW's and VALS's file requests (each Legal Aid Status Monitor's list) sit beside their matters, and
  // reading an application or approval letter moves the matching request on. Only the
  // status goes to the request; the letter stays here.
  const showApplications = brand === "lacw" || brand === "vals";
  const [applicationsRefresh, setApplicationsRefresh] = useState(0);
  const moveRequestsForLetter = async (fields = {}, clientName = "") => {
    if (!showApplications || !clientName) return;
    try {
      const { requests } = await fetchFileRequests(brand);
      const updates = requestUpdatesForLetter(requests, {
        documentType: fields.documentType,
        clientName,
        extensionNumber: fields.extensionNumber,
      });
      for (const { request, status } of updates) {
        await saveRequestStatus(request, status);
        toast.success(`File request for ${request.clientName} moved to "${requestStatusLabel(status)}".`);
      }
      if (updates.length) setApplicationsRefresh((count) => count + 1);
    } catch {
      // The letter is read either way; the request can be moved by hand.
    }
  };
  const [syncStatus, setSyncStatus] = useState(() => getAidPlannerSyncStatus());
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const { changed } = await syncMattersWithPortal(brand);
      if (cancelled) return;
      if (openFocusMatter() || !changed) return;
      setMattersTrigger((count) => count + 1);
      setPlanner((prev) => {
        const fresh = loadMatterFromDevice(prev.matterId);
        return fresh ? { ...fresh } : prev;
      });
    };
    const onStatus = (event) => setSyncStatus(event.detail || getAidPlannerSyncStatus());
    const onFocus = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener(AID_PLANNER_SYNC_EVENT, onStatus);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") sync(); }, 60000);
    sync();
    return () => {
      cancelled = true;
      window.removeEventListener(AID_PLANNER_SYNC_EVENT, onStatus);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    if (syncStatus.state === "conflict") setMattersTrigger((count) => count + 1);
  }, [syncStatus]);
  const syncBadge = (
    <span
      role="status"
      title={syncStatus.message}
      className={`inline-flex max-w-md items-center gap-1.5 truncate rounded-full border px-2.5 py-1 text-xs font-semibold ${
        syncStatus.state === "synced" ? "border-emerald-200 bg-emerald-50 text-emerald-800" :
        syncStatus.state === "conflict" || syncStatus.state === "error" ? "border-rose-200 bg-rose-50 text-rose-800" :
        syncStatus.state === "offline" || syncStatus.state === "local" ? "border-amber-200 bg-amber-50 text-amber-900" :
        "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      {syncStatus.state === "saving" || syncStatus.state === "syncing" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
      {syncStatus.message || "Matters are shared with your firm through the portal."}
    </span>
  );

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedAdapterId, setSelectedAdapterId] = useState("");
  const fileInputRef = useRef(null);

  const guidance = useMemo(() => assessAidPlanner(planner), [planner]);
  const selectedAdapter = useMemo(() => getPracticeManagerAdapter(selectedAdapterId), [selectedAdapterId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const queryMatterId = params.get("matterId");

    if (openFocusMatter()) return;

    if (queryMatterId) {
      const matter = loadMatterFromDevice(queryMatterId);
      if (matterInFirm(matter, brand)) {
        setPlanner(matter);
        setViewMode("editor");
        return;
      }
    }

    const lastId = getLastOpenedMatterId();
    const lastMatter = loadMatterFromDevice(lastId);
    if (matterInFirm(lastMatter, brand)) {
      setPlanner(lastMatter);
      if (mode === "editor") {
        setViewMode("editor");
      }
    }
  }, []);

  // Reload planner when worksheet/other tools save to this matter — handles both same-tab and cross-tab
  useEffect(() => {
    const reloadIfCurrentMatter = (matterId) => {
      if (!matterId) return;
      setPlanner((prev) => {
        if (prev.matterId !== matterId) return prev;
        const fresh = loadMatterFromDevice(matterId);
        return fresh ? { ...fresh } : prev;
      });
    };

    // Same-tab updates (e.g. worksheet embedded in same window)
    const handleMatterUpdated = (e) => reloadIfCurrentMatter(e?.detail?.matterId);
    window.addEventListener("matter-updated", handleMatterUpdated);

    // Cross-tab updates (worksheet opens in a new tab via target="_blank")
    const handleStorageChange = (e) => {
      if (e.key !== "aid-planner-matters-v1") return;
      // Parse updated store and find the current matter's ID
      setPlanner((prev) => {
        try {
          const store = e.newValue ? JSON.parse(e.newValue) : {};
          const fresh = store[prev.matterId];
          return fresh ? { ...fresh } : prev;
        } catch {
          return prev;
        }
      });
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("matter-updated", handleMatterUpdated);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    // Only save if the planner contains meaningful data to prevent polluting dashboard with empty rows
    const hasContent =
      planner.client.fullName ||
      planner.client.firstName ||
      planner.client.lastName ||
      planner.matter.court ||
      planner.aid.aidNumber ||
      planner.funding.vlaReference ||
      planner.notes;

    if (hasContent) {
      saveMatter({ ...planner, guidance });
      setLastOpenedMatterId(planner.matterId);
    }
  }, [planner, guidance]);

  const setClientField = (field, value) => {
    setPlanner((prev) => {
      const client = { ...prev.client, [field]: value };
      client.fullName = [client.firstName, client.lastName].filter(Boolean).join(" ");
      return updateTimestamp({ ...prev, client });
    });
  };

  const setMatterField = (field, value) => {
    setPlanner((prev) =>
      updateTimestamp(
        applyInferredCriminalPathway({
          ...prev,
          matter: { ...prev.matter, [field]: value },
        })
      )
    );
  };

  const setAidField = (field, value) => {
    setPlanner((prev) => {
      let next = {
        ...prev,
        aid: { ...prev.aid, [field]: value },
      };

      if (field === "aidType") {
        next.aid.aidTypeList = value
          .split(/;|,|\band\b/i)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      if (field === "aidTypeList") {
        next.aid.aidType = Array.isArray(value) ? formatProfessionalCostsList(value) : prev.aid.aidType;
      }

      if (field === "applicationStatus") {
        next = syncFundingFromApplicationStatus(next, value);
      }

      return updateTimestamp(next);
    });
  };

  const setFundingField = (field, value) => {
    setPlanner((prev) => updateTimestamp({ ...prev, funding: { ...prev.funding, [field]: value } }));
  };

  const setFinalisationField = (field, value) => {
    setPlanner((prev) => updateTimestamp({ ...prev, finalisation: { ...prev.finalisation, [field]: value } }));
  };

  const setApplicationField = (field, value) => {
    setPlanner((prev) => updateTimestamp({ ...prev, application: { ...prev.application, [field]: value } }));
  };

  const setNotes = (value) => {
    setPlanner((prev) => updateTimestamp({ ...prev, notes: value }));
  };

  const addAppearanceClaim = (initialValues = {}) => {
    setPlanner((prev) => {
      const baseClaim = createAppearanceClaim();
      const newClaim = { ...baseClaim, ...initialValues };
      
      // If an appearanceType is provided, try to apply fee defaults
      const finalClaim = initialValues.appearanceType && initialValues.appearanceType !== "Other"
        ? applyPlannerAppearanceFeeDefaults(newClaim, prev.matter.court, initialValues.appearanceType, prev.aid?.aidTypeList || [])
        : newClaim;

      return updateTimestamp({
        ...prev,
        appearanceClaims: [...(prev.appearanceClaims || []), finalClaim],
      });
    });
  };

  const updateAppearanceClaim = (id, field, value) => {
    setPlanner((prev) =>
      updateTimestamp({
        ...prev,
        appearanceClaims: (prev.appearanceClaims || []).map((item) => {
          if (item.id !== id) return item;
          if (field === "appearanceType") {
            const nextClaim = applyPlannerAppearanceFeeDefaults(
              item,
              prev.matter.court,
              value,
              prev.aid?.aidTypeList || []
            );
            return {
              ...nextClaim,
              customAppearanceType: value === "Other" ? nextClaim.customAppearanceType || "" : "",
              grantActionRequired: deriveGrantActionAfterOutcome(prev, {
                ...nextClaim,
                appearanceType: value,
              }),
            };
          }
          const nextClaim = { ...item, [field]: value };
          if (field === "notes" && !nextClaim.outcome) {
            nextClaim.outcome = inferOutcomeFromText(value);
          }
          if (field === "outcome" || field === "customAppearanceType" || field === "notes") {
            nextClaim.grantActionRequired = deriveGrantActionAfterOutcome(prev, nextClaim);
          }
          return nextClaim;
        }),
      })
    );
  };

  const removeAppearanceClaim = (id) => {
    setPlanner((prev) =>
      updateTimestamp({
        ...prev,
        appearanceClaims: (prev.appearanceClaims || []).filter((item) => item.id !== id),
      })
    );
  };

  const importAppearanceClaims = (importedClaims) => {
    setPlanner((prev) =>
      updateTimestamp({
        ...prev,
        appearanceClaims: [
          ...(prev.appearanceClaims || []),
          ...importedClaims.map((claim) => {
            const base = createAppearanceClaim();
            const appearanceType = claim.appearanceType || "Other";
            const withDefaults =
              appearanceType && appearanceType !== "Other"
                ? applyPlannerAppearanceFeeDefaults(base, prev.matter.court, appearanceType, prev.aid?.aidTypeList || [])
                : { ...base, appearanceType };
            const nextClaim = {
              ...withDefaults,
              date: claim.date || withDefaults.date,
              appearanceType,
              customAppearanceType: claim.customAppearanceType || "",
              outcome: claim.outcome || inferOutcomeFromText(claim.notes || ""),
              notes: claim.notes || "",
              importSource: claim.importSource || "",
              claimedUnits: claim.claimedUnits || "",
            };
            return {
              ...nextClaim,
              grantActionRequired: deriveGrantActionAfterOutcome(prev, nextClaim),
            };
          }),
        ],
      })
    );
  };

  const handleExportCalendar = () => {
    downloadPlannerIcs(planner, guidance);
  };

  const handleConfirmPracticeManager = (adapter, selectedMatter) => {
    setSelectedAdapterId(adapter.id);
    if (selectedMatter) {
      setPlanner((prev) => applyImportedMatter(prev, adapter, selectedMatter));
    }
    setImportModalOpen(false);
  };

  const resetPlanner = () => {
    setPlanner(createPlanner());
    setSelectedAdapterId("");
  };

  const handleLetterUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsParsingLetter(true);
    setLetterError("");

    try {
      const payload = await parseAidLetterUpload(file);
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      handleAidLetterParsed(payload, file.name, fileData);
    } catch (error) {
      setLetterError(error instanceof Error ? error.message : "Could not parse the uploaded aid letter.");
    } finally {
      setIsParsingLetter(false);
      event.target.value = "";
    }
  };

  const handleDeleteMatter = (matterIdToDelete) => {
    if (window.confirm("Are you sure you want to delete this matter planner? This cannot be undone.")) {
      deleteLocalMatter(matterIdToDelete);
      const remainingMatters = listLocalMatters(brand);
      if (remainingMatters.length > 0) {
        setPlanner(remainingMatters[0]);
      } else {
        setPlanner(createPlanner());
      }
      setMattersTrigger(prev => prev + 1);
    }
  };

  // Reads letters (and planner backups) into this firm's matters: uploads from the computer
  // and letters fetched from ATLAS both come through here. newMatterFor(payload) decides
  // whether a letter that matches no matter starts a new one (uploads always do).
  // Returns one outcome per file so the ATLAS panel can say what happened to each letter.
  // clientNames (file name → name) is used when the letter itself doesn't name the client.
  const importLetterFiles = async (files, { newMatterFor = () => true, clientNames = new Map() } = {}) => {
    if (!files || files.length === 0) return { successCount: 0, failCount: 0, outcomes: [] };

    setIsParsingBulk(true);
    setBulkParseSummary(null);
    setBulkProgress(`Parsing ${files.length} file${files.length > 1 ? "s" : ""}...`);

    // --- Step 1: Parse all PDFs in parallel ---
    const parseResults = await Promise.all(
      Array.from(files).map(async (file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "json") {
          try {
            const text = await file.text();
            return { type: "json", file, data: parsePlannerJson(text) };
          } catch (err) {
            console.error(`Failed to parse JSON planner file ${file.name}:`, err);
            return { type: "error", file };
          }
        } else if (ext === "pdf") {
          try {
            const payload = await parseAidLetterUpload(file);
            const fileData = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            return { type: "pdf", file, payload, fileData };
          } catch (err) {
            console.error(`Failed to parse VLA letter file ${file.name}:`, err);
            return { type: "error", file };
          }
        }
        return { type: "unknown", file };
      })
    );

    // --- Step 2: Sort PDFs into processing order so dependencies are met ---
    // Order: initial app request → ext app requests → initial approval → ext approvals → claim letters
    const letterPriority = (result) => {
      if (result.type !== "pdf") return 99;
      const docType = result.payload?.fields?.documentType || "";
      const extNum = result.payload?.fields?.extensionNumber;
      const isExt = extNum !== undefined && extNum !== null && String(extNum) !== "" && String(extNum) !== "0";
      if (docType === "application_request" && !isExt) return 0;   // Initial application
      if (docType === "application_request" && isExt) return 1;    // Extension applications
      if (docType === "grant_approval_letter" && !isExt) return 2; // Initial approval
      if (docType === "grant_approval_letter" && isExt) return 3;  // Extension approvals
      if (docType === "claim_letter") return 4;                    // Claim letters
      return 5;                                                    // Unknown
    };

    const jsonResults = parseResults.filter(r => r.type === "json");
    const pdfResults = parseResults
      .filter(r => r.type === "pdf")
      .sort((a, b) => letterPriority(a) - letterPriority(b));
    const orderedResults = [...jsonResults, ...pdfResults];

    // --- Step 3: Apply in order ---
    const parsedClients = [];
    const outcomes = parseResults
      .filter(r => r.type === "error" || r.type === "unknown")
      .map(r => ({ fileName: r.file.name, state: "failed" }));
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < orderedResults.length; i++) {
      const result = orderedResults[i];
      setBulkProgress(`Applying ${i + 1} of ${orderedResults.length} (${result.file.name})...`);

      if (result.type === "json") {
        try {
          saveMatter(result.data);
          parsedClients.push(result.data);
          successCount++;
        } catch (err) {
          console.error(`Failed to save JSON planner file ${result.file.name}:`, err);
          outcomes.push({ fileName: result.file.name, state: "failed" });
          failCount++;
        }
      } else if (result.type === "pdf") {
        try {
          const { file, payload, fileData } = result;
          const fields = payload?.fields || {};
          // The reader's own name for the client (from the letter's heading or the claim's
          // Client Name) comes first; the wording and file-name guesses are only a fallback.
          const clientFullName = String(fields.clientFullName || "").trim() || clientNames.get(file.name) || extractClientName(file, payload);
          const existingMatters = listLocalMatters(brand);

          const findMatch = (list) => list.find(m => {
            if (fields.aidNumber && m.aid?.aidNumber && fields.aidNumber === m.aid.aidNumber) return true;
            const inRef = (fields.practitionerReference || "").trim().toLowerCase();
            const exRef = (m.client?.fileNumber || "").trim().toLowerCase();
            if (inRef && exRef && inRef === exRef) return true;
            const inClean = clientFullName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/^dear/g, "");
            const exClean = (m.client?.fullName || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace(/^dear/g, "");
            if (inClean && exClean) {
              if (inClean === exClean) return true;
              if (inClean.length >= 5 && exClean.length >= 5) {
                if (inClean.includes(exClean) || exClean.includes(inClean)) return true;
              }
            }
            return false;
          });

          let basePlanner = findMatch(parsedClients) || findMatch(existingMatters);

          if (!basePlanner && !newMatterFor(payload)) {
            outcomes.push({ fileName: file.name, state: "no-matter", clientName: clientFullName });
            continue;
          }

          let updatedPlanner;
          if (basePlanner) {
            updatedPlanner = mergeParsedFieldsIntoPlanner(basePlanner, payload, file.name, fileData);
          } else {
            const nameParts = clientFullName.split(" ");
            const freshPlanner = createPlanner();
            const populated = {
              ...freshPlanner,
              matterId: `matter-${Date.now()}-${i}`,
              client: {
                ...freshPlanner.client,
                firstName: nameParts[0] || "",
                lastName: nameParts.slice(1).join(" ") || "",
                fullName: clientFullName,
                fileNumber: (() => {
                  const raw = (fields.practitionerReference || "").trim();
                  if (!raw) return "";
                  if (/^page\s+\d+\s+of\s+\d+$/i.test(raw)) return "";
                  if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(raw)) return "";
                  if (raw.split(/\s+/).length > 4) return "";
                  return raw;
                })(),
              }
            };
            updatedPlanner = mergeParsedFieldsIntoPlanner(populated, payload, file.name, fileData);
          }

          const syncedPlanner = syncFundingFromApplicationStatus(updatedPlanner, updatedPlanner.aid.applicationStatus);
          const withPathway = applyInferredCriminalPathway(syncedPlanner);
          const finalPlanner = updateTimestamp(withPathway);
          const savedPlanner = { ...finalPlanner, guidance: assessAidPlanner(finalPlanner) };

          saveMatter(savedPlanner);
          moveRequestsForLetter(fields, savedPlanner.client?.fullName || clientFullName);

          outcomes.push({
            fileName: file.name,
            state: basePlanner ? "read" : "new-matter",
            clientName: savedPlanner.client?.fullName || clientFullName,
            matterId: savedPlanner.matterId,
          });

          const existingIdx = parsedClients.findIndex(m => m.matterId === savedPlanner.matterId);
          if (existingIdx >= 0) {
            parsedClients[existingIdx] = savedPlanner;
          } else {
            parsedClients.push(savedPlanner);
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to apply VLA letter file ${result.file.name}:`, err);
          outcomes.push({ fileName: result.file.name, state: "failed" });
          failCount++;
        }
      } else {
        outcomes.push({ fileName: result.file.name, state: "failed" });
        failCount++;
      }
    }

    setIsParsingBulk(false);
    setBulkProgress("");
    setMattersTrigger(prev => prev + 1);

    if (parsedClients.length > 0) {
      setPlanner(parsedClients[0]);
    }

    setBulkParseSummary({
      successCount,
      failCount,
      clients: parsedClients.map(c => ({
        matterId: c.matterId,
        fullName: c.client.fullName || "Unnamed Client"
      })),
    });

    return { successCount, failCount, outcomes };
  };

  // Requisitions read from ATLAS go on their matter (by aid number, then name). One for a
  // client the Aid Planner doesn't have yet starts a matter, so none is lost.
  const importAtlasRequisitions = (requisitions = []) => {
    const outcomes = [];
    for (const req of requisitions) {
      try {
        let base = findRequisitionMatter(listLocalMatters(brand), req);
        const state = base ? "read" : "new-matter";
        if (!base) {
          const fresh = createPlanner();
          const fullName = atlasClientName(req.clientName || req.client);
          const [firstName = "", ...rest] = fullName.split(" ");
          base = {
            ...fresh,
            matterId: `matter-${Date.now()}-req${outcomes.length}`,
            client: { ...fresh.client, firstName, lastName: rest.join(" "), fullName },
            aid: { ...fresh.aid, aidNumber: req.fileId || "" },
          };
        }
        const updated = updateTimestamp(applyRequisition(base, req));
        saveMatter({ ...updated, guidance: assessAidPlanner(updated) });
        outcomes.push({ key: req.key, state, clientName: updated.client?.fullName || "", matterId: updated.matterId });
      } catch (err) {
        console.error("Failed to record ATLAS requisition:", err);
        outcomes.push({ key: req.key, state: "failed" });
      }
    }
    if (requisitions.length) setMattersTrigger(prev => prev + 1);
    return outcomes;
  };

  const answerRequisition = (req) => {
    const matter = listLocalMatters(brand).find((m) => m.matterId === req.matterId);
    if (!matter) return;
    const updated = updateTimestamp(markRequisitionAnswered(matter, req.id));
    saveMatter({ ...updated, guidance: assessAidPlanner(updated) });
    setMattersTrigger(prev => prev + 1);
    toast.success(`Requisition for ${req.clientName || "this client"} marked answered.`);
  };

  const handleUnifiedUpload = async (event) => {
    await importLetterFiles(event.target.files);
    event.target.value = "";
  };



  const stats = useMemo(() => {
    const list = localMatters;
    const total = list.length;
    const activeGrants = list.filter(m => m.aid?.applicationStatus === "Aid granted" || m.funding?.grantInPlace).length;
    const pendingApps = list.filter(m => 
      m.aid?.applicationStatus === "Application applied for - Pending" || 
      m.aid?.applicationStatus === "Aid submitted - application pending" || 
      m.aid?.applicationStatus === "Aid applied for" ||
      m.aid?.applicationStatus === "Pending" ||
      m.aid?.applicationStatus === "Requisition to be answered"
    ).length;
    const attentionRequired = list.filter(m => {
      const g = assessAidPlanner(m);
      return g.status === "missing_info" || g.status === "extension_review" || m.aid?.parsedAlerts?.length > 0 || m.aid?.conflictFlags?.length > 0;
    }).length;

    return { total, activeGrants, pendingApps, attentionRequired };
  }, [localMatters]);

  const grantReminders = useMemo(
    () => plannerReminders(localMatters, { availabilityFor: getClaimAvailabilityTable }),
    [localMatters]
  );
  // "Mark as applied". For an extension, this records it as requested, so the reminder
  // becomes "Chase VLA decision" until the extension letter (or a decision) is recorded.
  const markReminderDone = (reminder) => {
    const matter = listLocalMatters(brand).find((m) => m.matterId === reminder.entryId);
    if (!matter) return;
    if (reminder.kind === "extension") {
      saveMatter({
        ...matter,
        funding: { ...matter.funding, extensionRequested: true, extensionDecision: "", extensionRequestedAt: new Date().toISOString() },
      });
      setMattersTrigger((count) => count + 1);
      return;
    }
    saveMatter({
      ...matter,
      aid: {
        ...matter.aid,
        doneReminders: { ...(matter.aid?.doneReminders || {}), [reminder.key]: new Date().toISOString() },
      },
    });
    setMattersTrigger((count) => count + 1);
  };

  const filteredMatters = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return localMatters;
    return localMatters.filter(m => {
      const clientName = (m.client?.fullName || "").toLowerCase();
      const grantNum = (m.aid?.aidNumber || "").toLowerCase();
      const court = (m.matter?.court || "").toLowerCase();
      const fileRef = (m.client?.fileNumber || "").toLowerCase();
      return clientName.includes(query) || grantNum.includes(query) || court.includes(query) || fileRef.includes(query);
    });
  }, [localMatters, searchQuery]);

  const handleAidLetterParsed = (payload, fileName, fileData) => {
    const fields = payload?.fields || {};
    moveRequestsForLetter(fields, fields.clientFullName || planner.client?.fullName || "");
    const isNewMatterRequest =
      fields.documentType === "application_request" &&
      (fields.extensionNumber === "0" || !fields.extensionNumber);

    if (isNewMatterRequest) {
      const basePlanner = createPlanner();
      const merged = mergeParsedFieldsIntoPlanner(basePlanner, payload, fileName, fileData);
      const updatedPlanner = updateTimestamp(
        applyInferredCriminalPathway(
          syncFundingFromApplicationStatus(merged, merged.aid.applicationStatus)
        )
      );
      setPlanner(updatedPlanner);
      setViewMode("editor");
    } else {
      setPlanner((prev) => {
        const merged = mergeParsedFieldsIntoPlanner(prev, payload, fileName, fileData);
        return updateTimestamp(
          applyInferredCriminalPathway(
            syncFundingFromApplicationStatus(merged, merged.aid.applicationStatus)
          )
        );
      });
    }
  };

  const removeUploadedLetter = (id) => {
    setPlanner((prev) => {
      const letterToRemove = (prev.uploadedLetters || []).find((item) => item.id === id);
      const nextLetters = (prev.uploadedLetters || []).filter((item) => item.id !== id);
      const nextUploadedLetterName = nextLetters.map((l) => l.fileName).join(" | ");

      // If a claim letter is deleted, also remove the appearance tracker entries it created
      let nextAppearanceClaims = prev.appearanceClaims || [];
      if (letterToRemove) {
        const isClaimLetter =
          (letterToRemove.description || "").toLowerCase().includes("billing") ||
          (letterToRemove.description || "").toLowerCase().includes("claim");
        if (isClaimLetter) {
          nextAppearanceClaims = nextAppearanceClaims.filter(
            (c) => !(c.importSource === "claim_letter" && c.invoiceFileName === letterToRemove.fileName)
          );
        }
      }

      const nextPlanner = {
        ...prev,
        uploadedLetters: nextLetters,
        appearanceClaims: nextAppearanceClaims,
        aid: {
          ...prev.aid,
          uploadedLetterName: nextUploadedLetterName,
        },
      };
      if (nextLetters.length === 0) {
        nextPlanner.aid.parsedAlerts = [];
        nextPlanner.aid.conflictFlags = [];
        nextPlanner.aid.documentType = "";
        nextPlanner.aid.custodyStatus = "";
      }
      return updateTimestamp(nextPlanner);
    });
  };

  const clearAllUploadedLetters = () => {
    setPlanner((prev) =>
      updateTimestamp({
        ...prev,
        uploadedLetters: [],
        aid: {
          ...prev.aid,
          uploadedLetterName: "",
          parsedAlerts: [],
          conflictFlags: [],
          documentType: "",
          custodyStatus: "",
        },
      })
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" id="aid-planner-print">
      <style>{`
        @media print {
          body { background: white !important; }
          .aid-planner-no-print { display: none !important; }
          .aid-planner-print-card { box-shadow: none !important; border: 1px solid #d1d5db !important; }
          .aid-planner-print-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {viewMode === "dashboard" ? (
        <>
          {/* Dashboard Top Header Bar */}
          <div className="aid-planner-no-print bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!embedded && (
                <a
                  href={createPageUrl("Home")}
                  className="flex items-center gap-2 text-slate-600 hover:text-purple-600 transition-colors text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Portal
                </a>
              )}
              {syncBadge}
            </div>
            {!embedded && (
              <img
                src="/lacw-logo.png"
                alt="LACW Logo"
                className="h-7 object-contain"
              />
            )}
          </div>

          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            {/* Dashboard Welcome Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wide">
                  <CalendarDays className="w-3.5 h-3.5" />
                  Matters Hub
                </div>
                <h1 className="text-3xl font-extrabold text-slate-900 mt-2">Aid Planner Dashboard</h1>
                <p className="text-slate-500 mt-1">
                  Manage client grant details, bulk upload correspondence, and track claim items.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPlanner(createPlanner());
                    setViewMode("editor");
                  }}
                  className="h-11 rounded-xl bg-purple-600 hover:bg-purple-700 text-white px-5 text-sm font-semibold shadow-sm flex items-center gap-2 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Create New Matter
                </button>
              </div>
            </div>

            {/* Requisitions from ATLAS still to answer: these hold up a grant, so they come first */}
            <RequisitionsBanner
              requisitions={requisitionsOpen}
              onAnswered={answerRequisition}
              onOpen={(req) => {
                const matched = localMatters.find((m) => m.matterId === req.matterId);
                if (matched) {
                  setPlanner(matched);
                  setViewMode("editor");
                }
              }}
            />

            {/* Extensions and new grants due across every matter on this device */}
            {localMatters.length > 0 && (
              <GrantRemindersPanel
                reminders={grantReminders}
                onMarkApplied={markReminderDone}
                guideHref={brand === "lacw" || brand === "vals" ? getPortalToolPath(brand, "reminders-guide") : ""}
                onOpen={(reminder) => {
                  const matched = localMatters.find((m) => m.matterId === reminder.entryId);
                  if (matched) {
                    setPlanner(matched);
                    setViewMode("editor");
                  }
                }}
              />
            )}

            {/* Statistics Dashboard Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Matters", value: stats.total, color: "border-slate-200 text-slate-950" },
                { label: "Active Grants", value: stats.activeGrants, color: "border-emerald-200 text-emerald-700" },
                { label: "Pending Decisions", value: stats.pendingApps, color: "border-amber-200 text-amber-700" },
                { label: "Attention Required", value: stats.attentionRequired, color: "border-rose-200 text-rose-700" },
              ].map((item, idx) => (
                <div key={idx} className={`bg-white p-5 rounded-2xl border shadow-sm flex flex-col justify-between ${item.color}`}>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</span>
                  <span className="text-3xl font-black mt-2">{item.value}</span>
                </div>
              ))}
            </div>

            {/* Unified Upload Area */}
            <div
              onClick={() => unifiedUploaderRef.current?.click()}
              className="group cursor-pointer bg-white rounded-2xl border-2 border-dashed border-slate-200 hover:border-purple-400 p-8 flex flex-col items-center justify-center text-center transition-all hover:bg-purple-50/25"
            >
              <div className="w-14 h-14 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileUp className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mt-4">Upload letters or planner files</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md">
                Select or drag VLA Approval Letters (PDFs) or Planner Backups (JSONs). The system will automatically parse and register them.
              </p>
              <div className="mt-4 flex gap-2">
                <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold">PDF Letters</span>
                <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold">JSON Planners</span>
              </div>
              <input
                ref={unifiedUploaderRef}
                type="file"
                multiple
                accept=".pdf,.json,application/pdf,application/json"
                onChange={handleUnifiedUpload}
                className="hidden"
              />
            </div>

            {/* Fetch letters straight from the ATLAS inbox (needs the ATLAS Letters extension) */}
            <AtlasFetchPanel brand={brand} busy={isParsingBulk} onImport={importLetterFiles} result={atlasRun} onResult={setAtlasRun} onRequisitions={importAtlasRequisitions} />

            {/* Upload Organisation Tip */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 px-4 py-3 flex items-start gap-3 text-xs text-blue-800">
              <span className="flex-shrink-0 text-base leading-none mt-px">📁</span>
              <div className="leading-relaxed">
                <span className="font-bold text-blue-900">Recommended folder structure</span>{" "}
                — organise your downloaded VLA letters into three folders before uploading:
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-blue-200 rounded-md font-semibold text-blue-700">
                    📂 01_Applications
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-blue-200 rounded-md font-semibold text-blue-700">
                    📂 02_Outcome
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-blue-200 rounded-md font-semibold text-blue-700">
                    📂 03_Claims
                  </span>
                </div>
                <div className="mt-1.5 text-blue-700">
                  You can upload all folders at once — the system will automatically process them in the correct order.
                  <span className="font-semibold"> Always upload approval letters before claim letters</span> to ensure claims are matched correctly.
                </div>
              </div>
            </div>

            {/* Progress Bar (Bulk Uploads) */}
            {isParsingBulk && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 flex items-center gap-3 text-sm text-blue-900 animate-pulse">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                <div>
                  <span className="font-semibold">Processing files...</span>{" "}
                  <span className="text-blue-700">{bulkProgress}</span>
                </div>
              </div>
            )}

            {/* Success Summary Alert */}
            {bulkParseSummary && (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 flex items-start justify-between gap-3 text-sm text-emerald-900">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-800">Files Imported Successfully</div>
                    <div className="mt-1">
                      Successfully loaded <span className="font-bold">{bulkParseSummary.successCount}</span> files. 
                      {bulkParseSummary.failCount > 0 && (
                        <span> Failed to load <span className="font-bold text-rose-600">{bulkParseSummary.failCount}</span> files.</span>
                      )}
                    </div>
                    {bulkParseSummary.clients.length > 0 && (
                      <div className="mt-2 text-xs text-emerald-700 font-medium flex flex-wrap gap-2 items-center">
                        <span>Imported/updated clients:</span>
                        {bulkParseSummary.clients.map((c) => (
                          <button
                            key={c.matterId}
                            type="button"
                            onClick={() => {
                              const matched = listLocalMatters(brand).find((m) => m.matterId === c.matterId);
                              if (matched) {
                                setPlanner(matched);
                                setViewMode("editor");
                              }
                            }}
                            className="inline-flex px-2 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded font-bold transition-colors underline"
                          >
                            {c.fullName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkParseSummary(null)}
                  className="text-emerald-500 hover:text-emerald-800 text-xs font-semibold uppercase tracking-wider"
                >
                  Dismiss
                </button>
              </div>
            )}

            {showApplications && (
              <ApplicationsPanel
                brand={brand}
                matters={localMatters}
                refreshKey={applicationsRefresh}
                onOpenMatter={(matter) => {
                  setPlanner(matter);
                  setViewMode("editor");
                }}
                onStartMatter={(request) => {
                  const fresh = createPlanner();
                  const parts = request.clientName.split(" ").filter(Boolean);
                  setPlanner(updateTimestamp({
                    ...fresh,
                    client: {
                      ...fresh.client,
                      firstName: parts.slice(0, -1).join(" ") || parts[0] || "",
                      lastName: parts.length > 1 ? parts.at(-1) : "",
                      fullName: request.clientName,
                    },
                    notes: `Started from the file request "${request.subject}".`,
                  }));
                  setViewMode("editor");
                }}
              />
            )}

            {/* Client Matters List Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-lg font-bold text-slate-900">Client Matters & Grants</h2>
                <div className="w-full sm:max-w-xs relative">
                  <input
                    type="text"
                    placeholder="Search client, grant ref, court..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Client Name</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">VLA Ref / Grant ID</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Matter details</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Next Appearance</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Guidance Status</th>
                      <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredMatters.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                          {searchQuery ? "No matching clients found." : "No VLA client matters found. Upload VLA letters or planner backups to begin."}
                        </td>
                      </tr>
                    ) : (
                      filteredMatters.map((matter) => {
                        const mGuidance = assessAidPlanner(matter);
                        return (
                          <tr
                            key={matter.matterId}
                            className="hover:bg-slate-50 transition-colors group cursor-pointer"
                            onClick={() => {
                              setPlanner(matter);
                              setViewMode("editor");
                            }}
                          >
                            <td className="px-6 py-4">
                              <p className="font-bold text-slate-900 group-hover:text-purple-600 transition-colors">
                                {matter.client?.fullName || "Unnamed Client"}
                              </p>
                              {matter.client?.fileNumber && (
                                <p className="text-xs text-slate-400 mt-0.5 font-medium">File Ref: {matter.client.fileNumber}</p>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                              {matter.aid?.aidNumber || "No Grant Reference"}
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              <div>{matter.matter?.court || "No Court Set"}</div>
                              <div className="text-xs text-slate-400 font-medium">{matter.matter?.matterType || "Unassigned Type"}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {matter.matter?.nextAppearanceDate ? (
                                <div>
                                  <span className="font-medium">{matter.matter.nextAppearanceDate}</span>
                                  {matter.matter.appearanceType && (
                                    <span className="block text-xs text-slate-400">{matter.matter.appearanceType}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                mGuidance.status === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
                                mGuidance.status === "missing_info" ? "bg-amber-50 text-amber-700 border border-amber-100" :
                                "bg-rose-50 text-rose-700 border border-rose-100"
                              }`}>
                                {mGuidance.status.replaceAll("_", " ")}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => {
                                  setPlanner(matter);
                                  setViewMode("editor");
                                }}
                                className="px-3 py-1.5 text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                              >
                                Open
                              </button>
                              <button
                                onClick={() => {
                                  const mGuidanceAssessed = assessAidPlanner(matter);
                                  downloadPlannerJson(matter, mGuidanceAssessed);
                                }}
                                className="p-2 text-slate-400 hover:text-purple-600 rounded-lg hover:bg-slate-50 transition-colors"
                                title="Export backup (.json)"
                              >
                                <Plus className="w-4 h-4 rotate-45" />
                              </button>
                              <button
                                onClick={() => handleDeleteMatter(matter.matterId)}
                                className="p-2 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-slate-50 transition-colors"
                                title="Delete matter"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </>
      ) : (
        /* Planner Editor View */
        <>
          <div className="aid-planner-no-print bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!embedded && (
                <>
                  <a
                    href={createPageUrl("Home")}
                    className="flex items-center gap-2 text-slate-600 hover:text-purple-600 transition-colors text-sm font-medium"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Portal Home
                  </a>
                  <span className="text-slate-300">|</span>
                </>
              )}
              <button
                onClick={() => {
                  setViewMode("dashboard");
                  setMattersTrigger((prev) => prev + 1);
                }}
                className="flex items-center gap-2 text-slate-600 hover:text-purple-600 transition-colors text-sm font-medium"
              >
                ← Back to Dashboard
              </button>
              {syncBadge}
            </div>
            {planner.client?.fullName && (
              <div className="text-xs font-bold text-purple-700 bg-purple-50 border border-purple-100 px-3 py-1 rounded-full">
                Active Client: {planner.client.fullName}
              </div>
            )}
          </div>

          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="aid-planner-print-card bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold uppercase tracking-wide">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Portable matter planner
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 mt-3">Aid Planner</h1>
                </div>

                <PlannerActionsBar
                  onUploadLetterClick={() => fileInputRef.current?.click()}
                  onOpenPracticeManagerImport={() => setImportModalOpen(true)}
                  onExportCalendar={handleExportCalendar}
                  onReset={resetPlanner}
                />
                <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleLetterUpload} className="hidden" />
              </div>

              {/* Progress Bar (Editor Parsing) */}
              {isParsingBulk && (
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4 flex items-center gap-3 text-sm text-blue-900 animate-pulse">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Processing files...</span>{" "}
                    <span className="text-blue-700">{bulkProgress}</span>
                  </div>
                </div>
              )}

              {isParsingLetter && (
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4 flex items-center gap-3 text-sm text-blue-900 animate-pulse">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Parsing PDF letter...</span> Prefilling fields.
                  </div>
                </div>
              )}

              {letterError && (
                <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50/50 p-4 flex items-center gap-3 text-sm text-rose-800">
                  <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />
                  <div>{letterError}</div>
                </div>
              )}

              <div className="flex flex-col gap-6 mt-8">
                {/* Box 1: Matter Details */}
                <div>
                  <MatterDetailsTile
                    planner={planner}
                    setClientField={setClientField}
                    setMatterField={setMatterField}
                  />
                </div>

                {/* Box 2 & Box 5: Funding Info and Actions/Notes Side-by-Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FundingStatusTile
                    planner={planner}
                    setAidField={setAidField}
                    setFundingField={setFundingField}
                    setApplicationField={setApplicationField}
                    removeUploadedLetter={removeUploadedLetter}
                    clearAllUploadedLetters={clearAllUploadedLetters}
                  />
                  <ActionsNotesTile
                    guidance={guidance}
                    notes={planner.notes}
                    setNotes={setNotes}
                    selectedAdapter={selectedAdapter}
                    matterId={planner.matterId}
                  />
                </div>

                {/* Box 3: Appearance and Claims */}
                <div>
                  <AppearanceClaimsTile
                    planner={planner}
                    addAppearanceClaim={addAppearanceClaim}
                    updateAppearanceClaim={updateAppearanceClaim}
                    removeAppearanceClaim={removeAppearanceClaim}
                    importAppearanceClaims={importAppearanceClaims}
                  />
                </div>

                {/* Box 3b: Extension check (the Aid Extension Flowchart, filled in from this matter) */}
                <div>
                  <ExtensionCheckTile planner={planner} />
                </div>

                {/* Box 4: Finalisation and Closure */}
                <div>
                  <FinalisationClosureTile
                    planner={planner}
                    setFinalisationField={setFinalisationField}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <PracticeManagerImportModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        adapters={practiceManagerAdapters}
        onConfirmAdapter={handleConfirmPracticeManager}
      />
    </div>
  );
}
